/**
 * enrichExpandedTransactions.ts — Shared utility for populating expanded_transactions
 * on canvas node elements after an algorithm discovers new nodes/edges.
 *
 * This is the canonical way to populate the DATA LAYER (expanded_transactions[])
 * from the DISPLAY LAYER (canvas elements). All algorithms that discover new
 * nodes/edges should call this after their results are on canvas.
 *
 * Architecture:
 *   API/Algorithm result → canvas elements (fast visual feedback)
 *   → enrichExpandedTransactions() → expanded_transactions[] on each node
 *   → Node Inspector Transfers tab reads expanded_transactions[]
 *
 * Used by:
 *   - Follow the Money (after gold-path added)
 *   - SSE Investigation (after 'complete' event)
 *   - Backward/Forward Trace (after paths rendered)
 *   - Any future algorithm that adds nodes to canvas
 */

import { addressService } from '../services/api';
import { useTransactionStore, toExpandedTxs } from '../stores/useTransactionStore';
import { isPlaceholderId } from './placeholderNode';

export interface EnrichOptions {
    /** Chain for all addresses (single-chain enrichment) */
    chain: string;
    /** Max transactions to fetch per address */
    txLimit?: number;
    /** Concurrency limit for parallel fetches */
    concurrency?: number;
    /** Callback to update elements — accepts either:
     *  - React-style updater: (prev => next)
     *  - Zustand-style direct setter: (els) => void (will read current state internally)
     */
    setElements: ((updater: (prev: any[]) => any[]) => void) | ((els: any[]) => void);
    /** Function to get current elements (required when setElements is a direct setter) */
    getElements?: () => any[];
    /** Optional: only enrich these specific addresses (if omitted, enriches all nodes without expanded_transactions) */
    addresses?: string[];
    /** Optional: skip addresses that already have expanded_transactions */
    skipIfExists?: boolean;
}

/**
 * Fetch full transaction history for canvas nodes and populate their
 * expanded_transactions[] data property.
 *
 * This runs in the background (non-blocking) and silently merges
 * transactions into node element data via setElements().
 */
export async function enrichExpandedTransactions(opts: EnrichOptions): Promise<void> {
    const {
        chain,
        txLimit = 50,
        setElements,
        addresses,
        skipIfExists = true,
    } = opts;

    // Determine which addresses need enrichment
    let targetAddrs: string[];
    if (addresses) {
        targetAddrs = addresses;
    } else {
        // Read current elements to find nodes without expanded_transactions
        // (We can't read elements here since we only have setElements — caller should provide addresses)
        return;
    }

    // Drop synthesized placeholder ids (`ghost:…`, `__coinbase__:…`) that some
    // callers include when iterating raw canvas nodes. addressService.getTransactions
    // also guards these, but filtering here avoids burning Promise slots on guaranteed
    // 422s and keeps Network panels clean.
    targetAddrs = targetAddrs.filter(a => !isPlaceholderId(a));
    if (targetAddrs.length === 0) return;

    // Fetch transactions in parallel (controlled concurrency via Promise.allSettled)
    const fetchResults = await Promise.allSettled(
        targetAddrs.map(async (addr) => {
            const result = await addressService.getTransactions(chain, addr, txLimit, 0);
            return { addr, txs: result?.transactions || [] };
        })
    );

    // Build a map of addr → transactions for O(1) lookup
    const txMap = new Map<string, any[]>();
    for (const r of fetchResults) {
        if (r.status === 'fulfilled' && r.value.txs.length > 0) {
            txMap.set(r.value.addr, r.value.txs);
        }
    }

    if (txMap.size === 0) return;

    // Also write to useTransactionStore (global per-node store for Node Inspector)
    const txStore = useTransactionStore.getState();
    for (const [addr, txs] of txMap.entries()) {
        txStore.setNodeTransactions(addr, toExpandedTxs(txs, addr));
    }

    // Merge into expanded_transactions on each node element
    // Support both React-style updater and Zustand-style direct setter
    const applyUpdate = (updater: (prev: any[]) => any[]) => {
        if (opts.getElements) {
            // Zustand pattern: read current, transform, write back
            const current = opts.getElements();
            (setElements as (els: any[]) => void)(updater(current));
        } else {
            // React pattern: pass updater directly
            (setElements as (updater: (prev: any[]) => any[]) => void)(updater);
        }
    };
    applyUpdate(prev => prev.map(el => {
        if (el.data?.source) return el; // skip edges
        const txs = txMap.get(el.data.id);
        if (!txs) return el;

        // Skip if node already has expanded_transactions and skipIfExists is true
        if (skipIfExists && el.data.expanded_transactions?.length > 0) {
            // Still merge new txs not already present
            const existingHashes = new Set(
                el.data.expanded_transactions.map((t: any) => t.tx_hash)
            );
            const newTxs = txs.filter((tx: any) => tx.tx_hash && !existingHashes.has(tx.tx_hash));
            if (newTxs.length === 0) return el;
            return {
                ...el,
                data: {
                    ...el.data,
                    expanded_transactions: [...el.data.expanded_transactions, ...newTxs],
                    total_expanded_txs: el.data.expanded_transactions.length + newTxs.length,
                },
            };
        }

        return {
            ...el,
            data: {
                ...el.data,
                expanded_transactions: txs,
                total_expanded_txs: txs.length,
            },
        };
    }));
}

/**
 * Enrich expanded_transactions from edge data already on canvas.
 * Used when we don't want to make additional API calls — just reconstruct
 * from what's already rendered (gold-path trace_transactions, consolidated edges, etc.)
 *
 * This is the lightweight variant — no network calls, just reads existing edge data.
 */
export function enrichFromCanvasEdges(
    elements: any[],
    nodeId: string,
): any[] {
    const connectedEdges = elements.filter(
        el => el.data?.source && el.data?.target &&
            (el.data.source === nodeId || el.data.target === nodeId)
    );

    return connectedEdges.flatMap(el => {
        const isSender = el.data.source === nodeId;
        const counterparty = isSender ? el.data.target : el.data.source;
        const cpNode = elements.find(n => n.data?.id === counterparty && !n.data?.source);

        // Gold-path edges carry trace_transactions[] from backend enrichment
        if (Array.isArray(el.data.trace_transactions) && el.data.trace_transactions.length > 0) {
            return el.data.trace_transactions.map((tx: any) => ({
                tx_hash: tx.tx_hash || el.data.tx_hash,
                from_address: el.data.source,
                to_address: el.data.target,
                value: tx.value ?? el.data.value ?? 0,
                fee: tx.fee,
                timestamp: tx.timestamp || el.data.timestamp || null,
                block_height: tx.block_height,
                direction: isSender ? 'outgoing' as const : 'incoming' as const,
                counterparty,
                counterparty_entity: cpNode?.data?.entity_name || null,
                counterparty_type: cpNode?.data?.entity_type || null,
                counterparty_risk: cpNode?.data?.risk_score,
                token_symbol: tx.token_symbol || el.data.token_symbol || null,
                _group: el.data.edgeType === 'gold-path' ? 'gold-path' : undefined,
            }));
        }

        // Individual edge
        return [{
            tx_hash: el.data.tx_hash || el.data.id,
            from_address: el.data.source,
            to_address: el.data.target,
            value: typeof el.data.value === 'number' ? el.data.value : parseFloat(String(el.data.label || '0').replace(/[^0-9.]/g, '')) || 0,
            fee: el.data.fee,
            timestamp: el.data.timestamp || null,
            block_height: el.data.block_height,
            direction: isSender ? 'outgoing' as const : 'incoming' as const,
            counterparty,
            counterparty_entity: cpNode?.data?.entity_name || null,
            counterparty_type: cpNode?.data?.entity_type || null,
            counterparty_risk: cpNode?.data?.risk_score,
            token_symbol: el.data.token_symbol || null,
        }];
    });
}
